// must be first
import '@js-joda/locale_en-us'

import { CommonModule } from '@angular/common'
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http'
import { ErrorHandler, inject, NgModule, provideAppInitializer, signal } from '@angular/core'
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt'
import { DepartmentService } from '@ftr/api-core'
import { API_CONFIGURATION, AUTH_CONFIGURATION } from '@ftr/api-shared'
import { AppMode } from '@ftr/contracts/api/user'
import { CourtSystemsState } from '@ftr/data-court-system'
import {
  BreadcrumbModule,
  ConfirmationModalComponent,
  DialogComponent,
  LayoutService,
  PageComponent,
  PortalComponent,
  SimpleWindowRefService,
  SizesService,
  ToastModule,
  TrackingService,
} from '@ftr/foundation'
import { APP_MODE, HOME_ROUTE, HomeRoute, NAVIGATION_SHELL, NavigationShell } from '@ftr/routing-paths'
import {
  AuthRedirectInterceptor,
  CoreModule,
  jwtOptionsFactory,
  LaunchDarklyAttributeService,
  LoggingInterceptor,
} from '@ftr/ui-core'
import { FeatureFlagState, LAUNCH_DARKLY_CONFIG, LaunchDarklyModule } from '@ftr/ui-feature-flags'
import { CourtroomState } from '@ftr/ui-location'
import { ErrorHandlerService, OBSERVABILITY_CONFIGURATION } from '@ftr/ui-observability'
import { PORTAL_BRANDING } from '@ftr/ui-site-portal'
import {
  AnalyticsService,
  AUTH_TOKEN_STORAGE,
  AuthenticationService,
  AuthTokenStorageType,
  UserState,
} from '@ftr/ui-user'
import { VocabularyState } from '@ftr/ui-vocab'
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin'
import { provideStore } from '@ngxs/store'
import { provideAngularSvgIcon, SvgIconComponent } from 'angular-svg-icon'
import { AdminAppRoutingModule } from './admin-app-routing.module'
import { AdminAppComponent } from './admin-app.component'
import { HeaderModule } from './core/header/header.module'
import { ConfigurationService } from './services/configuration/configuration.service'
import { GlobalAdminHeaderSizeService } from './services/header-size/global-admin-header-size.service'
import { NGXS_OPTIONS } from './store/ngxs-options'

// Application wide providers
const APP_PROVIDERS = [
  { provide: APP_MODE, useValue: signal<AppMode>('admin').asReadonly() },
  { provide: NAVIGATION_SHELL, useValue: signal<NavigationShell>('redesign').asReadonly() },
  { provide: HOME_ROUTE, useValue: signal<HomeRoute>('root').asReadonly() },
  { provide: AUTH_TOKEN_STORAGE, useValue: signal<AuthTokenStorageType>('local').asReadonly() },
  { provide: PORTAL_BRANDING, useValue: signal(undefined).asReadonly() },
  { provide: ErrorHandler, useClass: ErrorHandlerService },
  { provide: TrackingService, useClass: AnalyticsService },
  { provide: LayoutService, useValue: LayoutService },
  { provide: SimpleWindowRefService, useClass: SimpleWindowRefService },
  { provide: API_CONFIGURATION, useClass: ConfigurationService },
  { provide: LAUNCH_DARKLY_CONFIG, useClass: ConfigurationService },
  { provide: OBSERVABILITY_CONFIGURATION, useClass: ConfigurationService },
  { provide: AUTH_CONFIGURATION, useClass: ConfigurationService },
  { provide: DepartmentService, useClass: DepartmentService },
  { provide: SizesService, useClass: GlobalAdminHeaderSizeService },
  // Wire up launch darkly to apply attributes globally from the store
  provideAppInitializer(() => {
    const initializerFn = (
      (service: LaunchDarklyAttributeService) => () =>
        service.observeAndApplyUserAttributes()
    )(inject(LaunchDarklyAttributeService))
    return initializerFn()
  }),
  provideAngularSvgIcon(),
]
const httpInterceptorProviders = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthRedirectInterceptor,
    multi: true,
  },
  { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true },
]
/**
 * `AppModule` is the main entry point into Angular's bootstrap process
 */
@NgModule({
  bootstrap: [AdminAppComponent],
  declarations: [AdminAppComponent],
  imports: [
    CommonModule,
    HeaderModule,
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory,
        deps: [AuthenticationService, ConfigurationService],
      },
    }),
    CoreModule.forRoot(),
    AdminAppRoutingModule,
    BreadcrumbModule,
    ToastModule,
    DialogComponent,
    PortalComponent,
    PageComponent,
    LaunchDarklyModule.forRoot(),
    SvgIconComponent,
    ConfirmationModalComponent,
  ],
  providers: [
    APP_PROVIDERS,
    httpInterceptorProviders,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    /**
     * Only register states here if they are required globally — needed by
     * app-level components or widely used across unrelated features (e.g. feature flag, vocab).
     *
     * States registered here are ALWAYS included in the main bundle, regardless
     * of whether the user ever visits the feature that uses them.
     *
     * For feature states, register them closer to where they are used
     * via providers: [provideStates([...])] in lazy-loaded modules or routes.
     */
    provideStore(
      [UserState, CourtSystemsState, VocabularyState, CourtroomState, FeatureFlagState],
      NGXS_OPTIONS,
      withNgxsReduxDevtoolsPlugin({
        disabled: true,
        name: 'FTR-ADMIN',
      }),
    ),
  ],
})
export class AdminAppModule {}
